import { useState, useEffect, useMemo } from 'react'
import type { User } from 'firebase/auth'
import * as Sentry from '@sentry/react'
import { Toaster } from 'sonner'
import { listOrgsForStaff, listClinicsForStaff, startImpersonation, type StaffOrg, type StaffClinic, type StaffViewRole } from '../services/staff'
import DataSyncRunHistory from './DataSyncRunHistory'
import ClinicHeatMap from './ClinicHeatMap'
import CustomerUsage from './CustomerUsage'
import SmsManagement from './SmsManagement'
import ScraperProxyPort from './ScraperProxyPort'
import PortalCodes from './PortalCodes'
import SmsActivity from './SmsActivity'
import BenefitsCaptureSettings from './BenefitsCaptureSettings'
import PayerAliasBoard from './PayerAliasBoard'

const ROLE_OPTIONS: { value: StaffViewRole; title: string; desc: string }[] = [
  { value: 'admin', title: 'Admin', desc: 'The whole practice, all clinics.' },
  { value: 'manager', title: 'Manager', desc: 'One clinic, full clinic access.' },
  { value: 'user', title: 'Staff', desc: 'One clinic, front-desk view.' },
]

interface StaffConsoleProps {
  user: User
  onSignOut: () => void
}

type Mode = 'view' | 'edit'

// Default to the 10 most-viewed practices; search can surface any of them.
const TOP_N = 10
// When searching, cap painted rows so the list stays snappy at 1000+ practices.
const RENDER_CAP = 50

// Which Firebase project this build talks to — surfaced as a badge so a staff
// operator never confuses dev/staging data with prod. Every staff function
// reads the same project the app is configured for; there is no cross-env mix.
const PROJECT_ID = (import.meta.env.VITE_FIREBASE_PROJECT_ID as string | undefined) ?? ''
const ENV_KIND: 'dev' | 'staging' | 'prod' =
  /prod/i.test(PROJECT_ID) ? 'prod' : /staging/i.test(PROJECT_ID) ? 'staging' : 'dev'

/**
 * Landing screen for VitalRev internal staff. Lists every customer org and
 * launches a session into one tenant. The operator picks a practice (or all),
 * a mode (read-only View, or Edit), and gives a reason — all audited.
 */
export default function StaffConsole({ user, onSignOut }: StaffConsoleProps) {
  const [section, setSection] = useState<'emulator' | 'runs' | 'heatmap' | 'usage' | 'smsManagement' | 'smsActivity' | 'scraperProxy' | 'portalCodes' | 'benefitsCapture' | 'payerMappings'>('emulator')
  // Set when the user clicks a "Pull failed" cell on the Heat Map — drives the
  // jump into Run History pre-filtered to that clinic + Failed.
  const [runsTarget, setRunsTarget] = useState<{ orgId: string; clinicId: string; date: string | null; pool: string } | null>(null)
  const [orgs, setOrgs] = useState<StaffOrg[] | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [filter, setFilter] = useState('')

  // Session-setup modal state
  const [pendingOrg, setPendingOrg] = useState<StaffOrg | null>(null)
  const [clinics, setClinics] = useState<StaffClinic[] | null>(null)
  const [clinicQuery, setClinicQuery] = useState('')
  const [clinicOpen, setClinicOpen] = useState(false)
  const [selectedClinicId, setSelectedClinicId] = useState('')
  const [role, setRole] = useState<StaffViewRole>('admin')
  const [mode, setMode] = useState<Mode>('view')
  const [reason, setReason] = useState('')
  const [launching, setLaunching] = useState(false)
  const [launchError, setLaunchError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    listOrgsForStaff()
      .then((list) => { if (!cancelled) setOrgs(list) })
      .catch((err) => {
        Sentry.captureException(err)
        if (!cancelled) setLoadError(err instanceof Error ? err.message : 'Failed to load organizations.')
      })
    return () => { cancelled = true }
  }, [])

  const isSearching = filter.trim().length > 0
  const filtered = useMemo(() => {
    if (!orgs) return []
    const q = filter.trim().toLowerCase()
    if (!q) return orgs // already sorted most-viewed-first by the backend
    return orgs.filter((o) => o.name.toLowerCase().includes(q) || o.orgId.toLowerCase().includes(q))
  }, [orgs, filter])
  // Default view = top 10 most-viewed this week; searching widens to RENDER_CAP.
  const visible = filtered.slice(0, isSearching ? RENDER_CAP : TOP_N)
  const hiddenCount = filtered.length - visible.length

  // Load the org's clinics when the modal opens. Resets live in the open handler.
  useEffect(() => {
    if (!pendingOrg) return
    let cancelled = false
    listClinicsForStaff(pendingOrg.orgId)
      .then((list) => { if (!cancelled) setClinics(list) })
      .catch((err) => {
        Sentry.captureException(err)
        if (!cancelled) setClinics([]) // fall back to all-clinics only
      })
    return () => { cancelled = true }
  }, [pendingOrg])

  const filteredClinics = useMemo(() => {
    if (!clinics) return []
    const q = clinicQuery.trim().toLowerCase()
    const list = q ? clinics.filter((c) => c.name.toLowerCase().includes(q)) : clinics
    return list.slice(0, RENDER_CAP)
  }, [clinics, clinicQuery])

  const selectedClinicName = clinics?.find((c) => c.clinicId === selectedClinicId)?.name ?? null
  const needsClinic = role !== 'admin'
  const canStart = reason.trim().length >= 3 && (!needsClinic || !!selectedClinicId) && !launching

  function openOrg(org: StaffOrg) {
    setPendingOrg(org); setReason(''); setLaunchError(null)
    setClinics(null); setSelectedClinicId(''); setClinicQuery(''); setClinicOpen(false)
    setRole('admin'); setMode('view')
  }

  function pickRole(next: StaffViewRole) {
    setRole(next)
    if (next === 'admin') { setSelectedClinicId(''); setClinicOpen(false) }
  }

  async function confirmImpersonation() {
    if (!pendingOrg || !canStart) return
    setLaunching(true)
    setLaunchError(null)
    try {
      await startImpersonation(pendingOrg.orgId, reason.trim(), {
        clinicId: selectedClinicId || null, role, mode,
      })
      // Pick up the new claims, then reload into the tenant.
      await user.getIdToken(true)
      window.location.reload()
    } catch (err) {
      Sentry.captureException(err)
      setLaunchError(err instanceof Error ? err.message : 'Could not start the session.')
      setLaunching(false)
    }
  }

  return (
    <div className="staff-shell">
      <Toaster position="bottom-right" richColors closeButton expand />
      <aside className="staff-sidebar">
        <div className="staff-sidebar__brand">VitalRev <span>Staff</span>
          <span className={`staff-env staff-env--${ENV_KIND}`} title={`Reading from ${PROJECT_ID || 'unknown project'}`}>{ENV_KIND.toUpperCase()}</span>
        </div>
        <nav className="staff-sidebar__nav">
          <button className={`staff-navitem${section === 'emulator' ? ' staff-navitem--active' : ''}`} onClick={() => setSection('emulator')}>Customer Emulator</button>
          <button className={`staff-navitem${section === 'runs' ? ' staff-navitem--active' : ''}`} onClick={() => { setSection('runs'); setRunsTarget(null) }}>Run History</button>
          <button className={`staff-navitem${section === 'heatmap' ? ' staff-navitem--active' : ''}`} onClick={() => setSection('heatmap')}>Clinic Heat Map</button>
          <button className={`staff-navitem${section === 'usage' ? ' staff-navitem--active' : ''}`} onClick={() => setSection('usage')}>Customer Usage</button>
          <button className={`staff-navitem${section === 'smsManagement' ? ' staff-navitem--active' : ''}`} onClick={() => setSection('smsManagement')}>SMS Management</button>
          <button className={`staff-navitem${section === 'smsActivity' ? ' staff-navitem--active' : ''}`} onClick={() => setSection('smsActivity')}>SMS Activity</button>
          <button className={`staff-navitem${section === 'scraperProxy' ? ' staff-navitem--active' : ''}`} onClick={() => setSection('scraperProxy')}>Scraper Proxy</button>
          <button className={`staff-navitem${section === 'portalCodes' ? ' staff-navitem--active' : ''}`} onClick={() => setSection('portalCodes')}>Portal Codes</button>
          <button className={`staff-navitem${section === 'benefitsCapture' ? ' staff-navitem--active' : ''}`} onClick={() => setSection('benefitsCapture')}>Benefits Capture</button>
          <button className={`staff-navitem${section === 'payerMappings' ? ' staff-navitem--active' : ''}`} onClick={() => setSection('payerMappings')}>Payer Mappings</button>
        </nav>
        <div className="staff-sidebar__account">
          <span className="staff-sidebar__email" title={user.email ?? ''}>{user.email}</span>
          <button className="staff-sidebar__signout" onClick={onSignOut}>Sign out</button>
        </div>
      </aside>

      <main className="staff-main">
      {section === 'runs' && (
        <DataSyncRunHistory
          key={runsTarget ? `${runsTarget.orgId}/${runsTarget.clinicId}/${runsTarget.date ?? ''}` : 'all'}
          initialOrgId={runsTarget?.orgId ?? null}
          initialClinicId={runsTarget?.clinicId ?? null}
          initialDate={runsTarget?.date ?? null}
          initialPool={runsTarget?.pool ?? null}
        />
      )}
      {section === 'heatmap' && (
        <ClinicHeatMap onOpenFailed={(row, dayIso) => { setRunsTarget({ orgId: row.orgId, clinicId: row.clinicId, date: dayIso, pool: 'revolution' }); setSection('runs') }} />
      )}
      {section === 'usage' && (
        <CustomerUsage onViewAs={(orgId, name) => {
          const o = orgs?.find((x) => x.orgId === orgId)
          openOrg(o ?? ({ orgId, name } as StaffOrg))
        }} />
      )}
      {section === 'smsManagement' && (
        <SmsManagement orgs={orgs} loadError={loadError} />
      )}
      {section === 'smsActivity' && <SmsActivity />}
      {section === 'scraperProxy' && (
        <ScraperProxyPort
          orgs={orgs}
          loadError={loadError}
          onSaved={(orgId, proxyPort) => setOrgs((prev) => prev ? prev.map((o) => (o.orgId === orgId ? { ...o, proxyPort } : o)) : prev)}
        />
      )}
      {section === 'portalCodes' && <PortalCodes />}
      {section === 'benefitsCapture' && (
        <BenefitsCaptureSettings
          orgs={orgs}
          loadError={loadError}
          onSaved={(orgId, next) => setOrgs((prev) => prev ? prev.map((o) => (o.orgId === orgId ? { ...o, ...next } : o)) : prev)}
        />
      )}
      {section === 'payerMappings' && (
        <PayerAliasBoard orgs={orgs} loadError={loadError} />
      )}
      {section === 'emulator' && (<div className="staff-emulator">
      <header className="staff-emulator__head">
        <div>
          <h1>Customer Emulator</h1>
          <p className="staff-console-subtitle">Open a customer's tenant to view or help them. Every session is logged.</p>
        </div>
      </header>
      <div className="staff-console-toolbar">
        <input
          type="search"
          placeholder="Search all practices by name or ID…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          aria-label="Search practices"
        />
        <span className="staff-console-count">
          {orgs ? (isSearching
            ? `${filtered.length} match${filtered.length === 1 ? '' : 'es'}`
            : 'Most viewed this week') : ''}
        </span>
      </div>

      {loadError && <p className="auth-error">{loadError}</p>}
      {!orgs && !loadError && <p className="staff-console-loading">Loading organizations…</p>}

      {orgs && (
        <>
          <ul className="staff-org-list">
            {visible.map((org) => (
              <li key={org.orgId} className="staff-org-row">
                <div className="staff-org-meta">
                  <span className="staff-org-name">{org.name}</span>
                  <span className="staff-org-id">{org.orgId}</span>
                  {org.createdAt && <span className="staff-org-created">since {org.createdAt.slice(0, 10)}</span>}
                </div>
                <div className="staff-org-actions">
                  {org.viewCount > 0 && (
                    <span className="staff-org-views" title="Sessions opened in the last 7 days">
                      {org.viewCount}× this week
                    </span>
                  )}
                  <button className="staff-view-button" onClick={() => openOrg(org)}>Open</button>
                </div>
              </li>
            ))}
            {filtered.length === 0 && <li className="staff-org-empty">No organizations match “{filter}”.</li>}
          </ul>
          {hiddenCount > 0 && (
            <p className="staff-console-more">
              {isSearching
                ? `Showing ${visible.length} of ${filtered.length} matches. Refine your search.`
                : `Showing the 10 most-viewed of ${filtered.length} practices. Search to open any other.`}
            </p>
          )}
        </>
      )}
      </div>)}
      </main>

      {pendingOrg && (
        <div className="staff-modal-backdrop" onClick={() => !launching && setPendingOrg(null)}>
          <div className="staff-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Open “{pendingOrg.name}”</h2>
            <p className="staff-modal-note">Sessions last 60 minutes and are logged.</p>

            <label className="staff-modal-label">View as</label>
            <div className="staff-mode-toggle">
              {ROLE_OPTIONS.map((r) => (
                <div
                  key={r.value}
                  className={`staff-mode-option${role === r.value ? ' staff-mode-option--active' : ''}`}
                  onClick={() => pickRole(r.value)}
                >
                  <span className="staff-mode-title">{r.title}</span>
                  <span className="staff-mode-desc">{r.desc}</span>
                </div>
              ))}
            </div>

            {needsClinic && (
              <>
                <label className="staff-modal-label">Clinic</label>
                <div className="staff-dropdown">
                  <button
                    type="button"
                    className="staff-dropdown-trigger"
                    onClick={() => setClinicOpen((o) => !o)}
                    disabled={launching || clinics === null}
                  >
                    <span className={selectedClinicName ? '' : 'staff-dropdown-placeholder'}>
                      {clinics === null ? 'Loading clinics…' : selectedClinicName ?? 'Select a clinic'}
                    </span>
                    <span className="staff-dropdown-caret" aria-hidden>▾</span>
                  </button>
                  {clinicOpen && clinics !== null && (
                    <div className="staff-dropdown-menu">
                      {clinics.length > 6 && (
                        <input
                          className="staff-modal-search staff-dropdown-search"
                          autoFocus
                          placeholder="Search clinics…"
                          value={clinicQuery}
                          onChange={(e) => setClinicQuery(e.target.value)}
                        />
                      )}
                      <ul className="staff-clinic-options">
                        {filteredClinics.map((c) => (
                          <li
                            key={c.clinicId}
                            className={`staff-clinic-option${selectedClinicId === c.clinicId ? ' staff-clinic-option--active' : ''}`}
                            onClick={() => { setSelectedClinicId(c.clinicId); setClinicOpen(false); setClinicQuery('') }}
                          >
                            {c.name}{c.status !== 'active' ? ` (${c.status})` : ''}
                          </li>
                        ))}
                        {filteredClinics.length === 0 && (
                          <li className="staff-clinic-option staff-clinic-empty">No clinics match.</li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              </>
            )}

            <label className="staff-modal-label">Access</label>
            <div className="staff-mode-toggle">
              <div
                className={`staff-mode-option${mode === 'view' ? ' staff-mode-option--active' : ''}`}
                onClick={() => setMode('view')}
              >
                <span className="staff-mode-title">View only</span>
                <span className="staff-mode-desc">Read-only. Nothing can change.</span>
              </div>
              <div
                className={`staff-mode-option staff-mode-option--edit${mode === 'edit' ? ' staff-mode-option--active' : ''}`}
                onClick={() => setMode('edit')}
              >
                <span className="staff-mode-title">Edit</span>
                <span className="staff-mode-desc">Make changes as their team would.</span>
              </div>
            </div>

            <label className="staff-modal-label">Reason</label>
            <textarea
              autoFocus
              placeholder={mode === 'edit'
                ? 'e.g. Fixing clinic timezone for ticket #214 at customer request'
                : 'e.g. Checking eligibility checks stuck pending for ticket #214'}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
            />
            {launchError && <p className="auth-error">{launchError}</p>}
            <div className="staff-modal-actions">
              <button className="dashboard-header__signout" onClick={() => setPendingOrg(null)} disabled={launching}>
                Cancel
              </button>
              <button
                className="staff-view-button"
                onClick={confirmImpersonation}
                disabled={!canStart}
              >
                {launching ? 'Starting…'
                  : needsClinic && !selectedClinicId ? 'Pick a clinic'
                    : mode === 'edit' ? 'Start editing' : 'Start viewing'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
